<template>
  <main>
    <div class="container">
      <div class="main-content-box">
        <div class="top-page-divider">
          <h3 class="page-title">Terms of Service</h3>
        </div>
        <hr />
        <h5 class="mt-4 mb-4">1. INTRODUCTION</h5>
        <p>
          These terms and conditions of use, together with our
          <a href="https://near-reality.com/privacy">Privacy Policy</a>,
          <a href="https://near-reality.com/rules/">Game Rules</a>, and all
          supplemental terms, documents and policies expressly referenced in
          them (together, the "Terms"), govern your use of the Near-Reality
          game, website, store and related services (the "Service") made
          available to you ("you"/"your") by Unity Games Studio, LLC ("Unity
          Games Studio", "we", "our", "us"), a company registered in the United
          States (registered number 801749378), with its registered office at
          Harbor Springs, Michigan, United States.
        </p>
        <p>
          Please read these Terms carefully. These Terms form a legally binding
          contract between you and us. By creating an account, or by
          downloading, installing, accessing or using the Service in any way,
          you agree to be bound by these Terms. If you do not agree to these
          Terms, do not create an account and do not use the Service. If you
          have already created an account or used the Service and do not agree
          to these Terms, you must immediately stop using the Service.
        </p>
        <p>
          We record your acceptance of these Terms, including the date, time
          and IP address, both when you register an account and each time you
          make a purchase.
        </p>
        <p>
          PLEASE KEEP A COPY OF THESE TERMS FOR FUTURE REFERENCE. WE MAY CHANGE
          THESE TERMS AS SET OUT IN SECTION 2 BELOW.
        </p>

        <hr />
        <h5 class="mt-4 mb-4">2. CHANGES TO THESE TERMS</h5>
        <p>
          We may change these Terms to reflect: (a) changes in applicable laws;
          (b) regulatory or security requirements; (c) relevant guidance or
          codes of practice; (d) technical changes to the Service; (e)
          improvements to clarity and consistency; or (f) any other reason
          within the sole discretion of Unity Games Studio.
        </p>
        <p>
          When we make material changes we will ask you to accept the updated
          Terms the next time you log in or make a purchase. Your continued use
          of the Service after any change constitutes acceptance of the updated
          Terms. Purchases are governed by the Terms in force, and accepted by
          you, at the time of the purchase.
        </p>

        <hr />
        <h5 class="mt-4 mb-4">3. ACCOUNTS, SUSPENSION AND TERMINATION</h5>
        <p>
          <b>3.1. <u>Your account.</u></b> You must register an account with a
          valid email address to use the Service and to make purchases. You are
          responsible for keeping your login credentials secure and for all
          activity on your account. You have no ownership or property interest
          in your account; all rights in and to accounts are and remain owned
          by Unity Games Studio.
        </p>
        <p>
          <b>3.2. <u>Termination by you.</u></b> You may terminate your account
          at any time by contacting us at support@near-reality.com.
        </p>
        <p>
          <b>3.3. <u>Suspension or termination by us.</u></b> We may suspend or
          terminate your account, and any other account we reasonably believe
          you own or control, without notice, if we reasonably determine that:
        </p>
        <ul class="bulletpoints">
          <li class="mb-4">
            you have breached any part of these Terms (including the Game
            Rules);
          </li>
          <li class="mb-4">
            doing so is in the best interests of our community or the Service,
            or is required to uphold a third party's rights;
          </li>
          <li class="mb-4">
            we have stopped offering the Service (we will normally post advance
            notice on our website or in game);
          </li>
          <li class="mb-4">
            you have failed to pay any amounts you owe us, or a payment made
            from your account has been reversed, disputed or charged back (see
            Section 6);
          </li>
          <li class="mb-4">
            you have made, or we reasonably suspect you have made, unauthorized
            use of someone else's payment method.
          </li>
        </ul>
        <p>
          <b>3.4. <u>Effect of termination.</u></b> On termination of your
          account for breach of these Terms, your license to the Service and
          to all Virtual Content ends immediately and all Virtual Content,
          balances and entitlements on the account are forfeited without
          compensation. Except where required by applicable law, you are not
          entitled to any refund of amounts paid before termination. Sections
          5, 6, 7 and 10 survive termination of your account or these Terms.
        </p>

        <hr />
        <h5 class="mt-4 mb-4">4. LIMITED LICENSE</h5>
        <p>
          We grant you a limited, personal, non-exclusive, non-transferable,
          non-sublicensable, revocable license to use and enjoy the Service
          and any Virtual Content for your individual, non-commercial
          entertainment purposes only, expressly conditioned on your compliance
          with these Terms. If your account is terminated, this license ends
          immediately. Unless expressly authorized by us in a signed written
          contract, you may not sell, copy, exchange, loan, reverse engineer,
          decompile, derive source code from, translate, lease, grant a
          security interest in, transfer, publish, assign or otherwise
          distribute any part of the Service or any of our intellectual
          property, including our computer code and Virtual Content.
        </p>

        <hr />
        <h5 class="mt-4 mb-4">5. VIRTUAL CONTENT AND PURCHASES</h5>
        <p>
          <b>5.1. <u>What is Virtual Content?</u></b> The Service may allow you
          to acquire virtual currency, items, ranks, memberships, bonds,
          cosmetics and other digital content ("Virtual Content"). Virtual
          Content may be acquired by purchasing it, earning it in game, or
          receiving it from another player where the Service allows.
        </p>
        <p>
          <b>5.2. <u>Virtual Content is licensed, not sold.</u></b> You have no
          ownership, property or monetary interest in any Virtual Content,
          regardless of how you acquired it or what you paid. What you receive
          is a personal, non-exclusive, non-transferable, non-sublicensable,
          revocable, limited license to use that Virtual Content within the
          Service only. Any purchase price paid is for this license and for the
          entertainment service of accessing the Virtual Content, not for the
          Virtual Content as property.
        </p>
        <p>
          <b>5.3. <u>No real-world value.</u></b> Virtual Content exists only
          inside the Service, has no cash or real-world value, does not
          constitute currency or property of any kind, and cannot be redeemed,
          exchanged, sold or transferred for money or anything of value outside
          the Service. Virtual Content may be modified, rebalanced, moved,
          re-priced or removed at any time, and terminates when your account or
          the Service terminates. If we entirely retire Virtual Content you
          recently purchased, we will provide a reasonable replacement.
        </p>
        <p>
          <b>5.4. <u>Immediate delivery.</u></b> Purchased Virtual Content is
          delivered automatically and immediately upon payment confirmation by
          crediting it to the Near-Reality account you designate at checkout.
          You agree that delivery is complete, and the purchased license fully
          furnished, at the moment the Virtual Content is credited to that
          account, as recorded in our server transaction logs. You are
          responsible for designating the correct account.
        </p>
        <p>
          <b>5.5. <u>All sales are final.</u></b> Because Virtual Content is
          delivered immediately and cannot be returned, all purchases are final
          and non-refundable, except: (a) where we fail to deliver the Virtual
          Content due to a fault in the Service; (b) verified duplicate
          transactions; or (c) where a refund is required by applicable law.
          Approved refunds are issued only to the original payment method, and
          the corresponding Virtual Content is removed from the account.
        </p>
        <p>
          <b>5.6. <u>EU and UK consumers.</u></b> By completing checkout you
          expressly request and consent to immediate delivery of the digital
          content before the end of any statutory 14-day cancellation period,
          and you acknowledge that you lose your right of withdrawal or
          cancellation once delivery begins. We confirm this consent and
          acknowledgment in your purchase confirmation email. Your mandatory
          statutory rights are otherwise unaffected.
        </p>
        <p>
          <b>5.7. <u>Payment authorization.</u></b> By completing a purchase
          you represent and warrant that: (a) you are at least 18 years old, or
          a parent or guardian who owns the payment method has authorized the
          purchase; (b) you are the owner or an authorized user of the payment
          method used; (c) all billing information you supply is accurate; and
          (d) you will not dispute or reverse a legitimate, delivered purchase.
          You acknowledge that we record the IP address, device identifiers,
          account identity and consent status of every purchase, and that we
          may provide these records to payment processors, card networks and
          financial institutions in connection with any payment dispute.
        </p>
        <p>
          <b>5.8. <u>Verification and high-value purchases.</u></b> For any
          purchase, and in particular for large or unusual purchases, we may:
          (a) require verification of your identity and of your authority to
          use the payment method, including government identification and a
          signed or recorded authorization statement; (b) delay, stage or split
          delivery of Virtual Content pending verification; (c) impose purchase
          limits; or (d) decline or refund a transaction. If you decline
          verification, we may cancel and refund the purchase.
        </p>

        <hr />
        <h5 class="mt-4 mb-4">6. CHARGEBACKS AND PAYMENT DISPUTES</h5>
        <p>
          <b>6.1. <u>Contact us first.</u></b> If you believe a charge is
          incorrect or unauthorized, you agree to contact us at
          support@near-reality.com and allow us 10 business days to investigate
          before initiating any chargeback, PayPal dispute or bank reversal.
        </p>
        <p>
          <b>6.2. <u>Consequences of an improper dispute.</u></b> If you
          initiate a chargeback, dispute or reversal of a payment for Virtual
          Content that was delivered in accordance with Section 5.4, we may, at
          our discretion and to the extent permitted by law:
        </p>
        <ul class="bulletpoints">
          <li class="mb-4">
            immediately suspend or terminate every account you own or control;
          </li>
          <li class="mb-4">
            revoke and remove any or all Virtual Content on those accounts,
            whether or not connected to the disputed payment, without
            compensation;
          </li>
          <li class="mb-4">
            treat the reversed amount, plus any chargeback, dispute or
            processing fees charged to us, as a debt immediately payable by
            you;
          </li>
          <li class="mb-4">
            set that debt off against any balance or entitlement on your
            accounts;
          </li>
          <li class="mb-4">bar you from future purchases; and</li>
          <li class="mb-4">
            recover our reasonable costs of collection.
          </li>
        </ul>
        <p>
          <b>6.3. <u>Reinstatement.</u></b> If a dispute is withdrawn or
          resolved in our favor, or you repay the reversed amount and
          associated fees, we may, but are not obliged to, reinstate the
          account and Virtual Content.
        </p>
        <p>
          <b>6.4.</b> Nothing in this Section limits any statutory right you
          have to dispute a payment. This Section describes the contractual
          consequences for your license and accounts when a delivered purchase
          is reversed.
        </p>

        <hr />
        <h5 class="mt-4 mb-4">7. REFUND POLICY</h5>
        <p>
          Refunds are governed by Section 5.5. In addition, and unless required
          by law, we will not refund any amounts paid in relation to Virtual
          Content where:
        </p>
        <ul class="bulletpoints">
          <li class="mb-4">
            you have been the victim of in-game scamming, as the Service warns
            against this (we will however try to assist you in recovering your
            account);
          </li>
          <li class="mb-4">
            your account has been suspended or terminated for violating these
            Terms, including the Game Rules;
          </li>
          <li class="mb-4">
            you have used unauthorized software to access the Service;
          </li>
          <li class="mb-4">you decide to cancel your account.</li>
        </ul>
        <p>
          If you wish to request a refund, please open a ticket via the
          <a href="https://near-reality.com/account/tickets/">Support section</a>
          with as much information as possible. Refunds are given at the sole
          discretion of Unity Games Studio, LLC. Your statutory rights are
          unaffected.
        </p>

        <hr />
        <h5 class="mt-4 mb-4">8. GAME RULES</h5>
        <p>
          You must comply with the current version of any rules, guidelines,
          codes of conduct or instructions specified for the Service,
          including our <a href="https://near-reality.com/rules/">Game Rules</a>,
          which are expressly incorporated into these Terms. Any use of the
          Service not in accordance with the Game Rules exceeds the scope of
          the license granted by these Terms and is prohibited.
        </p>

        <hr />
        <h5 class="mt-4 mb-4">9. THIRD PARTY SITES AND TECHNOLOGIES</h5>
        <p>
          THE SERVICE MAY INCLUDE LINKS TO INTERNET SITES OR OTHER TECHNOLOGIES
          SUPPLIED BY THIRD PARTIES, INCLUDING SITES AND TECHNOLOGIES WHICH
          ENABLE YOU TO PAY BY MEANS OF PAYMENT METHODS PROVIDED BY THIRD
          PARTIES. WE MAKE NO PROMISES ABOUT THOSE THIRD-PARTY SITES,
          TECHNOLOGIES OR THEIR CONTENT, PRODUCTS OR SERVICES, AS THESE ARE
          OUTSIDE OUR REASONABLE CONTROL.
        </p>
        <p>
          ANY THIRD-PARTY SITES OR TECHNOLOGIES SUGGESTED ARE NOT ENDORSED,
          CONTROLLED OR VERIFIED BY UNITY GAMES STUDIO. UNITY GAMES STUDIO DOES
          NOT GUARANTEE THAT ANY SUGGESTED TECHNOLOGIES WILL WORK ON YOUR
          COMPUTER OR BE VIRUS FREE. UNITY GAMES STUDIO IS NOT RESPONSIBLE FOR
          ANY DISRUPTION, PROBLEM, DAMAGE, DATA LOSS, COST OR INCONVENIENCE
          CAUSED BY SUGGESTED SITES OR TECHNOLOGIES, AND WILL NOT PROVIDE
          SUPPORT FOR ANY SITES OR TECHNOLOGIES WHICH ARE NOT ITS OWN.
        </p>

        <hr />
        <h5 class="mt-4 mb-4">10. GOVERNING LAW AND JURISDICTION</h5>
        <p>
          These Terms, and any dispute or claim arising out of or in connection
          with them, the Service, or any purchase (including non-contractual
          disputes or claims), are governed by and construed in accordance with
          the laws of the State of Michigan, United States, without regard to
          conflict-of-law rules.
        </p>
        <p>
          You and Unity Games Studio agree that the state and federal courts
          located in the State of Michigan have exclusive jurisdiction over any
          such dispute or claim, and each party irrevocably consents to the
          personal jurisdiction of, and venue in, those courts and waives any
          objection to them as an inconvenient forum.
        </p>
        <p>
          Before bringing any formal claim against us, you agree to first
          contact us at support@near-reality.com and attempt in good faith to
          resolve the matter informally for at least 30 days. If you are a
          consumer in a jurisdiction whose mandatory laws grant you rights or a
          forum that cannot be waived by contract, nothing in this Section
          deprives you of those mandatory rights; in all other respects,
          Michigan law and the Michigan courts govern.
        </p>
      </div>
    </div>
  </main>
</template>

<script>
export default {
  created() {
    document.title = "Terms - Near-Reality";
  },
};
</script>
